'use client';

/**
 * 会议议程独立查看页（v1.0）
 *
 * 路由：/meetingattendance/[meetingId]/agenda
 * 权限：登录用户即可访问（议程对参会人开放）；编辑按钮按 canManage 判定显隐
 *
 * 布局：返回会议详情 + 会议元信息 + Excel 表格风议程
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/lib/auth';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { AgendaReadonlyView } from '@/features/meeting-attendance/agenda/AgendaReadonlyView';

interface MeetingLite {
  id: string;
  title: string;
  status: string;
  startTime: string;
  endTime: string;
  creator?: { id: string };
}

export default function AgendaViewPage() {
  useAuthGuard();
  const params = useParams();
  const meetingId = useMemo(() => {
    const raw = params?.meetingId;
    return Array.isArray(raw) ? raw[0] : raw || '';
  }, [params]);
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const { user, hasAnyRole, isAdmin } = useAuth();

  const [meeting, setMeeting] = useState<MeetingLite | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMeeting = useCallback(async () => {
    if (!meetingId) return;
    try {
      setLoading(true);
      const data = await meetingAttendanceApi.getMeeting(meetingId);
      setMeeting(data as unknown as MeetingLite);
    } catch (err) {
      console.error('Failed to load meeting', err);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    loadMeeting();
  }, [loadMeeting]);

  const canManage = useMemo(() => {
    if (!user || !meeting) return false;
    if (isAdmin) return true;
    if (hasAnyRole(MEETING_MANAGEMENT_ROLES)) return true;
    if (meeting.creator?.id === user.id) return true;
    return false;
  }, [user, meeting, isAdmin, hasAnyRole]);

  return (
    <div className="container mx-auto max-w-7xl space-y-4 p-4">
      {/* 返回按钮 */}
      <div>
        <Link href={`/meetingattendance/meetings/${meetingId}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {tag.viewPage.backToMeeting}
          </Button>
        </Link>
      </div>

      {/* 标题 + 会议元信息 */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h1 className="text-xl font-bold text-gray-900">{tag.viewPage.pageTitle}</h1>
        {loading ? (
          <div className="mt-2 text-sm text-gray-500">{t.meetingAttendance.common.loading}</div>
        ) : meeting ? (
          <div className="mt-2 space-y-1">
            <div className="text-base font-medium text-gray-800">{meeting.title}</div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Calendar className="h-3 w-3" />
              <span>
                {new Date(meeting.startTime).toLocaleString()} –{' '}
                {new Date(meeting.endTime).toLocaleString()}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-2 text-sm text-gray-500">{tag.viewPage.meetingNotFound}</div>
        )}
      </div>

      {/* 议程表格 */}
      {meetingId && <AgendaReadonlyView meetingId={meetingId} canManage={canManage} />}
    </div>
  );
}
